import type { JavascriptSerializable } from '#layers/data-model/public';
import type { CollectionCodeOperation, CollectionCodeSnippetOptions } from './types';
import { joinURL } from 'ufo';
import { formatCurlSnippet } from './curl';
import { getRestReadOneKey } from './keys';
import { assertUnsupportedOperation } from './operation';
import { appendRestQuery, createRestReadQuery, createRestWriteQuery } from './rest-query';
import { resolveWriteData } from './sdk-payload';

/**
 * Generates a curl snippet for the REST API form of the requested operation.
 */
export function getCollectionRestSnippet(options: CollectionCodeSnippetOptions): string {
	const { collection, operation } = options;

	switch (operation) {
		case 'readFirst':
		case 'readMany': {
			const endpoint = joinURL(options.apiBaseUrl, options.workspaceSlug, 'items', collection.apiName);
			const url = appendRestQuery(endpoint, createRestReadQuery(options));

			return formatCurlSnippet({ method: 'GET', url });
		}
		case 'readOne': {
			const endpoint = joinURL(options.apiBaseUrl, options.workspaceSlug, 'items', collection.apiName, getRestReadOneKey(options));
			const url = appendRestQuery(endpoint, createRestReadQuery(options));

			return formatCurlSnippet({ method: 'GET', url });
		}
		case 'createMany':
		case 'createOne':
		case 'updateMany':
		case 'updateOne':
		case 'deleteMany':
		case 'deleteOne': {
			return getCollectionRestWriteSnippet(options);
		}
		default:
			return assertUnsupportedOperation(operation);
	}
}

/**
 * Generates a curl snippet for REST create, update, and delete operations.
 */
function getCollectionRestWriteSnippet(options: CollectionCodeSnippetOptions): string {
	const endpoint = getRestWriteEndpoint(options);
	const url = appendRestQuery(endpoint, createRestWriteQuery(options));
	const body = getRestWriteBody(options);

	return formatCurlSnippet({
		method: getRestWriteMethod(options.operation),
		url,
		body,
	});
}

/**
 * Maps collection write operations to their REST HTTP methods.
 */
function getRestWriteMethod(operation: CollectionCodeOperation): string {
	switch (operation) {
		case 'createMany':
		case 'createOne':
			return 'POST';
		case 'updateMany':
		case 'updateOne':
			return 'PATCH';
		case 'deleteMany':
		case 'deleteOne':
			return 'DELETE';
		default:
			return assertUnsupportedOperation(operation as never);
	}
}

/**
 * Builds the REST endpoint path for one-item and collection-wide write operations.
 */
function getRestWriteEndpoint(options: CollectionCodeSnippetOptions): string {
	const { collection, operation } = options;
	const baseEndpoint = joinURL(options.apiBaseUrl, options.workspaceSlug, 'items', collection.apiName);

	if (operation === 'updateOne' || operation === 'deleteOne') {
		return joinURL(baseEndpoint, getRestReadOneKey(options));
	}

	return baseEndpoint;
}

/**
 * Returns the JSON body used by REST write snippets, if the operation accepts one.
 */
function getRestWriteBody(options: CollectionCodeSnippetOptions): JavascriptSerializable {
	if (options.operation === 'createOne' || options.operation === 'createMany' || options.operation === 'updateOne' || options.operation === 'updateMany') {
		return resolveWriteData(options);
	}
}
